import React from "react";
import { Sparkles, ArrowRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

/**
 * Marketing CTA card that drives visitors from a data-heavy section into
 * Roni (in-page chat panel) or the consultation form directly below.
 * Visually mirrors the Compare-Areas CTA card on the homepage
 * (rounded-[2rem] sand card, gold eyebrow, editorial headline, pill button).
 *
 * Fires the same `roni:open` CustomEvent that the home Hero uses so the
 * AIChatWidget slides open in-place — no route change, no lost scroll.
 *
 * Consumer supplies:
 *   copy.en / copy.ar → { eyebrow, heading, headingAccent, body, cta }
 *   testId            → root data-testid (button gets `-roni-link` suffix)
 */
export default function RoniCTACard({ copy, testId }) {
  const { lang } = useLang();
  const isRTL = lang === "ar";
  const t = copy[isRTL ? "ar" : "en"];
  const openRoni = () => {
    try {
      window.dispatchEvent(new CustomEvent("roni:open"));
    } catch {
      /* ignore — the chat widget always listens once mounted */
    }
  };
  return (
    <section
      className="px-4 sm:px-8 lg:px-16 pt-2 pb-8 sm:pb-10 lg:pb-12"
      data-testid={testId}
    >
      <div className="mx-auto max-w-[1280px]">
        <button
          type="button"
          onClick={openRoni}
          data-testid={`${testId}-roni-link`}
          className="group relative block w-full text-start overflow-hidden rounded-[2rem] bg-propertic-sand/70 hover:bg-propertic-sand border hairline hover:border-propertic-gold/50 transition-all cursor-pointer"
        >
          <div className="relative grid md:grid-cols-[1fr_auto] gap-8 items-center px-6 sm:px-10 py-7 sm:py-9">
            <div className="min-w-0">
              <p className="font-mono-brand text-propertic-gold text-[10px] tracking-[0.24em] uppercase mb-3 inline-flex items-center gap-2">
                <Sparkles className="w-3 h-3" aria-hidden="true" />
                {t.eyebrow}
              </p>
              <h2 className="font-editorial text-[20px] sm:text-3xl leading-[1.15] text-propertic-ink">
                {t.heading}
                {t.headingAccent && (
                  <>
                    {" "}
                    <span className="font-italic-serif text-propertic-gold">
                      {t.headingAccent}
                    </span>
                  </>
                )}
              </h2>
              <p className="mt-3 text-propertic-ink-2/85 text-[13px] sm:text-[15px] leading-relaxed max-w-3xl">
                {t.body}
              </p>
            </div>
            <div className="flex items-center gap-2 text-propertic-navy md:justify-self-end whitespace-nowrap">
              <span className="inline-flex items-center h-11 px-6 rounded-full border border-propertic-navy/25 group-hover:border-propertic-navy bg-white/60 text-propertic-navy text-xs font-mono-brand tracking-[0.14em] uppercase transition-all">
                {t.cta}
              </span>
              <ArrowRight
                className={`w-5 h-5 ${isRTL ? "rtl-mirror" : ""} transition-transform group-hover:translate-x-1`}
              />
            </div>
          </div>
        </button>
      </div>
    </section>
  );
}
